/** @format */

import {
	Aggregation,
	hasDisplayableContent,
	Question,
	QuestionSeries,
	SpecificationThemeAggregation,
} from "@question-bank/shared";
import type { PaperEntry } from "./paper";
import { isWelshLang, type ExportLanguage } from "./language";

export type { QuestionNode } from "@question-bank/shared";

export const toStringArray = (value: string | string[] | undefined) => {
	if (Array.isArray(value)) {
		return value;
	} else if (value) {
		return [value];
	} else {
		return [];
	}
};

/**
 * Resolve a season's display label from a Core reference, tolerating legacy /
 * malformed shapes. Season should be a CoreRef ({ code, title? }), but older or
 * partially-migrated data may carry a plain string or an empty object. Returns
 * an empty string when nothing usable is present, so callers never render
 * "undefined".
 */
export const formatSeason = (season?: unknown): string => {
	if (!season) {
		return "";
	}
	if (typeof season === "string") {
		return season;
	}
	if (typeof season === "object") {
		const ref = season as { title?: unknown; code?: unknown };
		if (typeof ref.title === "string" && ref.title) {
			return ref.title;
		}
		if (typeof ref.code === "string" && ref.code) {
			return ref.code;
		}
	}
	return "";
};

export const formatSeries = (series?: { season?: unknown; year?: number }) => {
	if (!series) {
		return null;
	}
	const season = formatSeason(series.season);
	const year = series.year ?? "";
	// Never emit "undefined"; show whichever parts resolve.
	return [season, year].filter(Boolean).join(" ") || null;
};

/**
 * Overlays the counts from the currently-filtered aggregation set onto the
 * full (baseline) list of options. Every option in `full` is retained; any
 * option missing from `filtered` is returned with a count of 0 so the UI can
 * disable it rather than hide it. When `filtered` is not yet available the
 * baseline list is returned unchanged.
 */
export const mergeAggregationCounts = (
	full: Aggregation[] | undefined,
	filtered: Aggregation[] | undefined,
): Aggregation[] => {
	if (!filtered) {
		return full ?? [];
	}
	const counts = new Map(filtered.map((bucket) => [bucket.key, bucket.count]));
	return (full ?? []).map((bucket) => ({ key: bucket.key, count: counts.get(bucket.key) ?? 0 }));
};

/**
 * Hierarchical equivalent of {@link mergeAggregationCounts} for the
 * specification theme → topic → subTopic tree. Retains the full baseline tree
 * and overlays filtered counts, defaulting missing buckets to 0.
 */
export const mergeSpecificationCounts = (
	full: SpecificationThemeAggregation[] | undefined,
	filtered: SpecificationThemeAggregation[] | undefined,
): SpecificationThemeAggregation[] => {
	if (!filtered) {
		return full ?? [];
	}
	const themeMap = new Map(
		filtered.map((theme) => [
			theme.key,
			{
				count: theme.count,
				topics: new Map(
					theme.topics.map((topic) => [
						topic.key,
						{
							count: topic.count,
							subTopics: new Map(topic.subTopics.map((sub) => [sub.key, sub.count])),
						},
					]),
				),
			},
		]),
	);
	return (full ?? []).map((theme) => {
		const filteredTheme = themeMap.get(theme.key);
		return {
			key: theme.key,
			count: filteredTheme?.count ?? 0,
			topics: theme.topics.map((topic) => {
				const filteredTopic = filteredTheme?.topics.get(topic.key);
				return {
					key: topic.key,
					count: filteredTopic?.count ?? 0,
					subTopics: topic.subTopics.map((sub) => ({
						key: sub.key,
						count: filteredTopic?.subTopics.get(sub.key) ?? 0,
					})),
				};
			}),
		};
	});
};

export const resolveActiveLang = (language: string | undefined): ExportLanguage =>
	language?.toLowerCase().startsWith("cy") ? "cymraeg" : "english";

const welshAlphabet = "a b c ch d dd e f ff g ng h i j l ll m n o p ph r rh s t th u w y".split(" ");

const partLetter = (partIndex: number, language: ExportLanguage): string => {
	if (language === "cymraeg") {
		return welshAlphabet[partIndex] ?? String.fromCodePoint(97 + partIndex);
	}
	return String.fromCodePoint(97 + partIndex);
};

const transliterateSuffix = (suffix: string): string =>
	suffix.replace(/^\(([a-z])\)/, (_match, letter: string) => {
		return `(${welshAlphabet[letter.charCodeAt(0) - 97] ?? letter})`;
	});

/** label a part by its position in the assembled paper
 *
 * @param groupIndex - zero based position of the question group in the paper.
 * @param partIndex - zero-based position of the part within its group's displayable parts.
 * @param totalParts - Number of displayable parts in the group (1 collapses to the bare group number)
 * @param question - the part being labelled; whole group adds reuse its printed part suffix.
 * @param entry - the cart entry ; `selectedParts` switches to positional lettering.
 * @param language - the paper's language; Welsh papers letter parts down the Welsh alphabet.
 * @returns the display label e.g "3", "3(a)", "3(ch)"
 */

export const getPartLabel = (
	groupIndex: number,
	partIndex: number,
	totalParts: number,
	question: Question,
	entry: PaperEntry,
	language: ExportLanguage,
): string => {
	if (totalParts === 1) {
		return `${groupIndex + 1}`;
	}
	if (entry.selectedParts) {
		return `${groupIndex + 1}(${partLetter(partIndex, language)})`;
	}
	const suffix = question.questionNumber.slice(question.rootQuestionNumber.length);
	return `${groupIndex + 1}${isWelshLang(language) ? transliterateSuffix(suffix) : suffix}`;
};

export interface LabelledQuestion {
	question: Question;
	label: string;
	/**
	 * Series of the owning Paper (QB2-223), carried from the cart entry so the
	 * export can print "Question taken from … {season} {year}" without reading
	 * the fact off a bare Question. Undefined when the paper has not resolved.
	 */
	series?: QuestionSeries;
}

/** pair each displayable part of a group with its assembled paper label (see {@link getPartLabel}) */

export const labelledQuestions = (
	questions: Question[],
	entry: PaperEntry,
	groupIndex: number,
	language: ExportLanguage,
): LabelledQuestion[] => {
	const displayQuestions = questions.filter(hasDisplayableContent);
	const shouldKeepHeaders = !entry.selectedParts && displayQuestions.length > 1;
	const renderedQuestions = shouldKeepHeaders
		? questions.filter(
				(question) =>
					hasDisplayableContent(question) ||
					(question.questionNumber !== question.rootQuestionNumber &&
						displayQuestions.some(
							(part) =>
								part.questionNumber.length > question.questionNumber.length &&
								part.questionNumber.startsWith(question.questionNumber),
						)),
			)
		: displayQuestions;
	return renderedQuestions.map((question, partIndex) => ({
		question,
		label: getPartLabel(groupIndex, partIndex, displayQuestions.length, question, entry, language),
		series: entry.series,
	}));
};

/** Resolve the localised content for the selected language, falling back to the other. */
export const localisedContent = (part: Question, activeLang: ExportLanguage) =>
	isWelshLang(activeLang) ? (part.cy ?? part.en) : (part.en ?? part.cy);

export const formatChangeValue = (value: string): string => {
	try {
		return JSON.stringify(JSON.parse(value), null, 2);
	} catch {
		return value;
	}
};
